import React, { memo, useCallback } from "react";
import { Box, Flex, Text, Image as ChakraImage } from "@chakra-ui/react";
import { PhotoIcon, VariableIcon, PlusCircleIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import { a } from "../appColors";

const ROW_ICON = { width: "14px", height: "14px", flexShrink: 0 };

/** One line of the menu: an icon and a label. */
const MenuRow = memo(({ icon: Icon, label, onClick }) => (
  <Flex
    as="button"
    type="button"
    align="center"
    gap="3px"
    p="4px"
    w="100%"
    borderRadius="4px"
    color={a.text}
    fontSize="11px"
    lineHeight="16px"
    _hover={{ bg: a.hover }}
    onClick={onClick}
  >
    <Icon style={ROW_ICON} />
    <Text as="span">{label}</Text>
  </Flex>
));
MenuRow.displayName = "MenuRow";

/**
 * One of the question's figures, offered as a thumbnail.
 *
 * The badge is a tick once it is in the answer, so a student can see at a
 * glance which diagrams they have already taken across.
 */
const FigureChip = memo(({ figure, isAdded, onInsert }) => (
  <Box
    as="button"
    type="button"
    position="relative"
    w="55px"
    h="51px"
    flexShrink={0}
    borderRadius="8px"
    border="1px solid"
    borderColor={isAdded ? a.accent : a.text}
    overflow="hidden"
    bg={a.surface}
    cursor={isAdded ? "default" : "pointer"}
    opacity={isAdded ? 0.55 : 1}
    transition="opacity 0.12s, border-color 0.12s"
    _hover={isAdded ? {} : { borderColor: a.accent }}
    aria-label={isAdded ? `${figure.label} already added` : `Add ${figure.label} to your answer`}
    title={figure.label}
    onClick={() => !isAdded && onInsert(figure)}
  >
    <ChakraImage
      src={figure.image?.src}
      alt=""
      w="100%"
      h="100%"
      objectFit="cover"
      draggable={false}
    />
    <Box position="absolute" top="2px" right="2px" color={isAdded ? a.accent : a.text}>
      {isAdded ? (
        <CheckCircleIcon style={{ width: 14, height: 14 }} />
      ) : (
        <PlusCircleIcon style={{ width: 14, height: 14 }} />
      )}
    </Box>
  </Box>
));
FigureChip.displayName = "FigureChip";

/**
 * What the plus button opens (Figma 269:362).
 *
 * Upload and formula are the two things a student brings from outside; below
 * them sit the figures already printed in this question, so taking a diagram
 * into an answer is one click rather than a trip back up the page.
 */
const AddMenu = ({ figures, addedFigureIds, onUpload, onInsertFormula, onInsertFigure }) => {
  const handleUpload = useCallback(() => onUpload?.(), [onUpload]);
  const handleFormula = useCallback(() => onInsertFormula?.(), [onInsertFormula]);

  return (
    <Flex
      direction="column"
      gap="8px"
      p="8px"
      borderRadius="5px"
      bg={a.surfaceSubtle}
      filter="drop-shadow(0px 4px 5px rgba(0,0,0,0.07))"
      minW="180px"
    >
      <MenuRow icon={PhotoIcon} label="Upload Image" onClick={handleUpload} />
      <MenuRow icon={VariableIcon} label="Insert Formula" onClick={handleFormula} />

      {figures.length > 0 && (
        <Flex direction="column" gap="12px" px="7px" pb="4px">
          <Text fontSize="9px" lineHeight="16px" color={a.text}>
            Add Image from question
          </Text>
          {/* Papers can print several diagrams in one question, so the row
              scrolls rather than pushing the menu wider than the answer box. */}
          <Flex gap="9px" align="center" overflowX="auto" pb="2px" maxW="260px">
            {figures.map((figure) => (
              <FigureChip
                key={figure.id}
                figure={figure}
                isAdded={addedFigureIds.has(figure.id)}
                onInsert={onInsertFigure}
              />
            ))}
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};

export default memo(AddMenu);
